import { ReferenceType } from '../deposit-split.types'
import { useTranslation, Trans } from 'react-i18next'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { useDepositSplitForm } from './deposit-split-form.hook'
import { Controller } from 'react-hook-form'
import { FormItem } from 'hic-components'
import { Grid, Typography } from '@vapor/material'
import { DepositSplitLookup } from '../deposit-split-lookup/deposit-split-lookup'
import { z } from 'zod'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { useTenant } from 'src/app/hooks/context/context.hook'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import { watch } from 'fs'

type DepositSplitDialogProps = {
  propertyId: string
  id: string
  amount: number
  quotaReferenceDescription: string
  quotaReferenceType: ReferenceType
  quotaReferenceId: string
  onClose: () => void
  onSave: (args: { referenceType: ReferenceType; referenceDescription: string; billId?: string; reservationId?: string; reservedResourceId?: string; destinationAmount?: number }) => void
  operationType: 'edit' | 'split'
}

export const DepositSplitDialog = (props: DepositSplitDialogProps) => {
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency()
  const { currency } = useTenant()

  const { control, errors, depositSplitSchema, onSubmit, setValue } = useDepositSplitForm(props.amount, (data) => {
    const { type, description, billId, reservationId, reservedResourceId } = data.reference
    props.onSave({ referenceType: type, referenceDescription: description, billId, reservationId, reservedResourceId, destinationAmount: data.amountToSplit })
  })
  type DepositSplit = z.TypeOf<typeof depositSplitSchema>

  setValue('operation', props.operationType)

  return (
    <FormLayout
      title={props.operationType === 'edit' ? t('pms.accounting.documents.deposit_split.edit_title') : t('pms.accounting.documents.deposit_split.split_title')}
      appearance={{ type: 'DIALOG', open: true, onClose: props.onClose, maxWidth: '350px' }}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={{
            clickHandler: onSubmit,
            text: t('commons.save'),
          }}
          cancelButton={{
            clickHandler: props.onClose,
          }}
        />
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="titleSmall"> {formatCurrency(props.amount, currency)}</Typography>
          <div>
            {props.quotaReferenceType === 'bill' ? (
              <Trans i18nKey={'pms.accounting.documents.deposit_split.initial_amount.bill'} values={{ billReferenceId: props.quotaReferenceDescription }} />
            ) : (
              <Trans i18nKey={'pms.accounting.documents.deposit_split.initial_amount.reservation'} values={{ reservationReferenceId: props.quotaReferenceDescription }} />
            )}
          </div>
        </Grid>
        {props.operationType === 'split' && (
          <Grid item xs={12}>
            <Controller<DepositSplit, 'amountToSplit'>
              name={'amountToSplit'}
              control={control}
              render={({ field }) => (
                <FormItem //
                  fullWidth
                  error={Boolean(errors.amountToSplit)}
                  errorMessage={errors.amountToSplit?.message}
                  fieldLabel={t('pms.accounting.documents.deposit_split.amount_to_split')}
                >
                  <HicAmountField currency={currency} size="small" value={field.value} onChange={field.onChange} />
                </FormItem>
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Controller<DepositSplit, 'reference'>
            name={'reference'}
            control={control}
            render={({ field }) => (
              <FormItem //
                fullWidth
                error={Boolean(errors.reference)}
                errorMessage={errors.reference?.type?.message}
                fieldLabel={
                  props.operationType === 'split' //
                    ? t('pms.accounting.documents.deposit_split.split.reference_id')
                    : t('pms.accounting.documents.deposit_split.edit.reference_id')
                }
              >
                <DepositSplitLookup propertyId={props.propertyId} onChange={field.onChange} />
              </FormItem>
            )}
          />
        </Grid>
      </Grid>
    </FormLayout>
  )
}
